import type { ReactNode, Ref } from "react";
import { AlertCircleIcon, MailIcon } from "./icons";
import { Spinner } from "./ui";

/**
 * The hosted auth screens' pieces (Claude Design 4c). The card itself is
 * the layout's (routes/auth AuthLayout, `.rago-auth-card`), so moving
 * between sign in, sign up and forgot swaps only what's inside it; each
 * screen renders an AuthBody and, below it, an AuthFooter.
 */

/** The card's body: heading, fields and the primary action, 24px apart. */
export function AuthBody({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-6 px-8 pt-9 pb-[30px]">{children}</div>;
}

/** Centred title and subtitle. A `ref` makes the title a focus target for a
 * view that replaces the form (forgot's "Check your email"). */
export function AuthHeading(props: {
  title: string;
  ref?: Ref<HTMLHeadingElement>;
  children?: ReactNode;
}) {
  return (
    <div className="text-center">
      <h1
        ref={props.ref}
        tabIndex={props.ref ? -1 : undefined}
        className="rago-display text-[32px] leading-[1.1] text-balance text-[var(--rago-text-1)] outline-none max-sm:text-[28px]"
      >
        {props.title}
      </h1>
      {props.children ? (
        <p className="mt-2 text-[15px] leading-normal text-balance text-[var(--rago-text-3)]">
          {props.children}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A label over its control, an optional note or link at the label row's
 * right (`aside`), the error line under it (id `${id}-error` — the control
 * points aria-describedby there). The aside comes after the control in the
 * DOM, so Tab runs email → password → "Forgot?" and a screen reader hears
 * the link after the field it belongs to.
 */
export function AuthField(props: {
  id: string;
  label: string;
  aside?: ReactNode;
  error?: string;
  /** The error came back from the server: announce it. A submit's own
   * check is read out through the focus move and aria-describedby instead. */
  alert?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2">
      <label
        htmlFor={props.id}
        className="col-start-1 row-start-1 self-baseline text-[13.5px] font-medium text-[var(--rago-text-2)]"
      >
        {props.label}
      </label>
      <div className="col-span-2 row-start-2">{props.children}</div>
      {props.aside ? <div className="col-start-2 row-start-1 self-baseline">{props.aside}</div> : null}
      {props.error ? (
        <FieldError id={`${props.id}-error`} alert={props.alert} className="col-span-2 row-start-3">
          {props.error}
        </FieldError>
      ) : null}
    </div>
  );
}

/** One error line: a circled "!" and the words, in the error red. */
export function FieldError(props: {
  id: string;
  alert?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <p
      id={props.id}
      role={props.alert ? "alert" : undefined}
      className={`flex items-start gap-1.5 text-[13.5px] leading-snug text-[var(--rago-error)] ${props.className ?? ""}`}
    >
      <AlertCircleIcon size={15} className="mt-0.5 shrink-0" />
      <span className="min-w-0">{props.children}</span>
    </p>
  );
}

/** Where a mailed link went: an envelope, "Sent to" and the address, which
 * wraps rather than hides (forgot password's "Check your email", the Change
 * email dialog's). */
export function SentToTile(props: { email: string }) {
  return (
    <div className="flex items-center gap-3.5 rounded-[14px] border border-[rgba(255,255,255,0.06)] bg-[rgba(0,0,0,0.28)] px-4 py-3.5">
      <span
        aria-hidden
        className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-[rgba(62,207,142,0.14)] text-[var(--rago-ok)]"
      >
        <MailIcon size={18} />
      </span>
      <div className="min-w-0">
        <p className="text-[12.5px] text-[var(--rago-text-dim)]">Sent to</p>
        <p className="text-[15px] font-medium [overflow-wrap:anywhere] text-[var(--rago-text-1)]">
          {props.email}
        </p>
      </div>
    </div>
  );
}

/** Under a hairline: "New to Rago? Create an account →". The link is the
 * screen's own <Link className="rago-auth-switch"> with its arrow. */
export function AuthFooter(props: { prompt: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-[22px] px-8 pb-7">
      <div
        aria-hidden
        className="h-px bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent)]"
      />
      <p className="text-center text-[14.5px] text-[var(--rago-text-3)]">
        {props.prompt} {props.children}
      </p>
    </div>
  );
}

/** A screen's chunk still loading (routes are code-split): a spinner in the
 * card instead of the router's full-height default. */
export function AuthPending() {
  return (
    <div role="status" aria-label="Loading" className="grid min-h-[280px] place-items-center text-[var(--rago-accent)]">
      <Spinner size={28} />
    </div>
  );
}
